/* Stage 2 "Dashboard" · 2.2 — the four figure cells of the Engagements tab (Status | Sign-offs | Ready to lock | Report).
   One source per figure (Code Health Report Part C): every cell reads GET /api/engagements/{id}/figures (backend/firm_dashboard.py,
   one request per listed engagement, loading state per cell) and links to the page that owns the figure:
     Status      → the stage's page (C-1: stage derived from the work done; the stored engagements.status is not shown)
     Sign-offs   → Audit Trail › Sign-offs tab (C-2: signoffs.table rollup n / total)
     Ready       → File Assembly lock readiness table (C-3: lock_readiness.full_check; "Ready" or "n open items")
     Report      → Auditor's Report (C-4: opinion of the latest Final version + w12_assembly.report_date, DD-MMM-YYYY)
   Chip colours are the card's. Dates never ISO. Texts in lib/i18n/1480_ds_d.js (EN + AR). */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate } from "@/lib/dt";
import { asArray } from "@/lib/asArray";
import NumPair from "@/components/NumPair";
import UpdatingNote, { isMissing, isUpdating } from "@/components/dashboard/StoredUpdating";   // D-10

export const STAGE_STYLE = {
  planning:   { bg: "#E0E7FF", fg: "#3730A3", to: "/planning" },
  fieldwork:  { bg: "#CFFAFE", fg: "#155E75", to: "/vouching" },
  completion: { bg: "#FEF3C7", fg: "#92400E", to: "/completion/clearance-gate" },
  reporting:  { bg: "#EDE9FE", fg: "#5B21B6", to: "/reporting/auditors-report" },
  locked:     { bg: "#DCFCE7", fg: "#166534", to: "/close/file-assembly" },
};
export const OPINION_STYLE = {
  unmodified:    { bg: "#DCFCE7", fg: "#166534" },
  qualified:     { bg: "#FEF3C7", fg: "#92400E" },
  adverse:       { bg: "#FEE2E2", fg: "#991B1B" },
  disclaimer:    { bg: "#FEE2E2", fg: "#991B1B" },
  not_concluded: { bg: "#F3F4F6", fg: "#374151" },
};
export const SIGNOFFS_PATH = "/audit-trail?tab=signoffs";
export const READY_PATH = "/close/file-assembly";
export const REPORT_PATH = "/reporting/auditors-report";

export const FIGURES_POLL_MS = 8000;     // D-10: a stale / not-yet-calculated block is read again (at most 15 times)
const FIGURES_POLL_MAX = 15;

/** One GET /engagements/{id}/figures per listed engagement → { [id]: { loading, data, error } }. D-10: the route answers from
 *  the STORED block (cheap); a stale / missing one carries stored.state and is read again until the worker refreshed it. */
export function useEngagementFigures(engagements) {
  const ids = asArray(engagements).map((e) => e.id).filter(Boolean);
  const key = ids.join("|");
  const [figures, setFigures] = useState({});
  const seen = useRef(new Set());
  useEffect(() => {
    let cancelled = false;
    const fresh = ids.filter((id) => !seen.current.has(id));
    if (!fresh.length) return undefined;
    fresh.forEach((id) => seen.current.add(id));
    setFigures((prev) => { const n = { ...prev }; fresh.forEach((id) => { n[id] = { loading: true, data: null, error: false }; }); return n; });
    const timers = [];
    const fetchOne = (id, n) => {
      api.get(`/engagements/${id}/figures`, { _silent: true })
        .then((r) => {
          if (cancelled) return;
          setFigures((prev) => ({ ...prev, [id]: { loading: false, data: r.data || null, error: false } }));
          if (isUpdating(r.data && r.data.stored) && n < FIGURES_POLL_MAX) timers.push(setTimeout(() => fetchOne(id, n + 1), FIGURES_POLL_MS));
        })
        .catch(() => { if (!cancelled) setFigures((prev) => ({ ...prev, [id]: { loading: false, data: null, error: true } })); });
    };
    fresh.forEach((id) => fetchOne(id, 0));
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return figures;
}

const Chip = ({ style, testid, children, title }) => (
  <span data-testid={testid} title={title}
        className="inline-block max-w-full whitespace-normal break-words rounded-md px-2 py-0.5 text-xs font-medium leading-5"
        style={{ backgroundColor: style.bg, color: style.fg }}>
    {children}
  </span>
);
const Loading = ({ id }) => <span data-testid={`eng-fig-loading-${id}`} className="text-muted-foreground" aria-busy="true">…</span>;
const Blank = ({ t }) => <span className="text-muted-foreground">{t("dsD.figuresUnavailable")}</span>;
// D-10: a block never calculated → "Updating…" (never a 0); a stale one keeps its value, the Status cell says "Updating… (as of …)"
const Updating = ({ id, t }) => <span data-testid={`eng-fig-updating-${id}`} className="text-amber-700 dark:text-amber-300" aria-busy="true">{t("sbC.updating")}</span>;
const cellLink = "block rounded-sm hover:underline focus:outline-none focus:ring-2 focus:ring-slate-300";

export function StatusCell({ eng, fig, t, onOpen }) {
  if (!fig || fig.loading) return <Loading id={eng.id} />;
  if (isMissing(fig.data?.stored)) return <Updating id={eng.id} t={t} />;
  const stage = fig.data?.status?.stage;
  const st = STAGE_STYLE[stage];
  if (!st) return <Blank t={t} />;
  const label = t(`dsD.stage.${stage}`);
  return (
    <Link to={st.to} onClick={() => onOpen && onOpen(eng.id)} className={cellLink}
          data-testid={`eng-status-${eng.id}`} title={t("dsD.linkStage").replace("{stage}", label)}>
      <Chip style={st} testid={`eng-status-chip-${eng.id}`}>{label}</Chip>
      <UpdatingNote stored={fig.data?.stored} t={t} testid={`eng-fig-stale-${eng.id}`} className="mt-0.5" />
    </Link>
  );
}

export function SignoffsCell({ eng, fig, t, onOpen }) {
  if (!fig || fig.loading) return <Loading id={eng.id} />;
  if (isMissing(fig.data?.stored)) return <Updating id={eng.id} t={t} />;
  const s = fig.data?.signoffs;
  if (!s || typeof s.total !== "number") return <Blank t={t} />;
  return (
    <Link to={SIGNOFFS_PATH} onClick={() => onOpen && onOpen(eng.id)} className={`${cellLink} num-cell tabular-nums`}
          data-testid={`eng-signoffs-${eng.id}`} title={t("dsD.linkSignoffs")}>
      <NumPair parts={[Number(s.signed || 0), Number(s.total)]} />
    </Link>
  );
}

export function ReadyCell({ eng, fig, t, onOpen }) {
  if (!fig || fig.loading) return <Loading id={eng.id} />;
  if (isMissing(fig.data?.stored)) return <Updating id={eng.id} t={t} />;
  const l = fig.data?.lock;
  if (!l) return <Blank t={t} />;
  const n = Number(l.open_items || 0);
  const ready = !!l.ready && n === 0;
  const text = ready ? t("dsD.ready") : (n === 1 ? t("dsD.openItem") : t("dsD.openItems").replace("{n}", String(n)));
  return (
    <Link to={READY_PATH} onClick={() => onOpen && onOpen(eng.id)} className={cellLink}
          data-testid={`eng-ready-${eng.id}`} data-ready={ready ? "1" : "0"} title={t("dsD.linkReady")}>
      <span className={ready ? "font-medium text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}>{text}</span>
    </Link>
  );
}

export function ReportCell({ eng, fig, t, onOpen }) {
  if (!fig || fig.loading) return <Loading id={eng.id} />;
  if (isMissing(fig.data?.stored)) return <Updating id={eng.id} t={t} />;
  const r = fig.data?.report;
  if (!r) return <Blank t={t} />;
  const op = OPINION_STYLE[r.opinion] ? r.opinion : "not_concluded";
  const date = r.report_date ? fmtDate(r.report_date) : t("dsD.noDate");
  return (
    <Link to={REPORT_PATH} onClick={() => onOpen && onOpen(eng.id)} className={`${cellLink} space-y-0.5`}
          data-testid={`eng-report-${eng.id}`} title={t("dsD.linkReport")}>
      <Chip style={OPINION_STYLE[op]} testid={`eng-opinion-chip-${eng.id}`}>{t(`dsD.opinion.${op}`)}</Chip>
      <div className="font-mono text-[11px] text-muted-foreground" dir="ltr" data-testid={`eng-report-date-${eng.id}`}>{date}</div>
    </Link>
  );
}
